import React from 'react';
import { Box, Button, Divider, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import LinkIcon from '@mui/icons-material/Link';
import AppsIcon from '@mui/icons-material/Apps';
import PageHeader from '../../../components/page-header';
import { useMenuController } from './context/menu-controller';
import { useMenuState } from './context/menu-context';
import { SortableList } from '../components/sortable-list';
import { EmptyState } from '../../../components/empty-state';
import { MenuRow } from './components/menu-row';
import { MenuDrawer } from './components/menu-drawer';
import { IMenuResponse } from '../../../model/apm/menu/response/menu-response';

function PanelHeader({ icon, title, subtitle, actionLabel, onAction, disabled }: {
    icon: React.ReactNode; title: string; subtitle: string; actionLabel?: string; onAction?: () => void; disabled?: boolean;
}) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {icon}
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" noWrap>{title}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">{subtitle}</Typography>
                </Box>
            </Box>
            {actionLabel && (
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAction} disabled={disabled} sx={{ flexShrink: 0 }}>
                    {actionLabel}
                </Button>
            )}
        </Box>
    );
}

export function MenuComponent() {
    const ctrl = useMenuController();
    const state = useMenuState();
    const selectedMenu = state.menus.find((m) => m.id === state.selectedMenuId);
    const fixedMenus = state.menus.filter((m) => m.key);
    const customMenus = state.menus.filter((m) => !m.key);

    const renderMenuRow = (menu: IMenuResponse, dragHandle: React.ReactNode) => (
        <MenuRow
            dragHandle={dragHandle}
            title={menu.title}
            slug={menu.slug}
            url={menu.url}
            customPageId={menu.customPageId}
            isDefault={menu.isDefault}
            isVisible={menu.isVisible}
            canEdit={menu.canEdit}
            selected={menu.id === state.selectedMenuId}
            extra={menu.canAddItems && <Typography variant="caption" sx={{ ml: 0.5, flexShrink: 0 }}>· {menu.items?.length ?? 0} item(ns)</Typography>}
            onClick={() => ctrl.selectMenu(menu.id)}
            onToggleVisibility={() => ctrl.handleToggleMenuVisibility(menu)}
            onEdit={() => ctrl.openMenuDrawer(menu)}
            onDelete={() => ctrl.handleDeleteMenu(menu)}
        />
    );

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Menus"
                    subtitle="Defina a navegação do portal da entidade. Arraste para alterar a ordem de exibição."
                    breadcrumbs={[{ label: 'Menus' }]}
                />
            </Grid>

            <Grid item xs={12} md={5}>
                <Paper sx={{ p: 2, height: '100%' }}>
                    <PanelHeader
                        icon={<MenuIcon color="primary" />}
                        title="Menu principal"
                        subtitle="Menus fixos do portal: arraste para reordenar e adicione itens"
                    />
                    {state.loadingMenus && <LinearProgress sx={{ mb: 1 }} />}
                    <SortableList
                        items={fixedMenus}
                        getId={(m) => m.id}
                        canDrag={(m) => m.canReorder}
                        onReorder={ctrl.handleReorderMenus}
                        renderItem={renderMenuRow}
                    />

                    <Divider sx={{ my: 2 }} />

                    <PanelHeader
                        icon={<AppsIcon color="primary" />}
                        title="Mais opções"
                        subtitle={`${customMenus.length} menu(s) exibidos no fim da barra`}
                        actionLabel="Novo menu"
                        onAction={() => ctrl.openMenuDrawer()}
                    />
                    {!state.loadingMenus && customMenus.length === 0 ? (
                        <EmptyState icon={<AppsIcon />} message="Nenhum menu em Mais opções" actionLabel="Cadastrar o primeiro menu" onAction={() => ctrl.openMenuDrawer()} />
                    ) : (
                        <SortableList
                            items={customMenus}
                            getId={(m) => m.id}
                            onReorder={ctrl.handleReorderMenus}
                            renderItem={renderMenuRow}
                        />
                    )}
                </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
                <Paper sx={{ p: 2, height: '100%' }}>
                    <PanelHeader
                        icon={<LinkIcon color="primary" />}
                        title={selectedMenu ? `Itens de "${selectedMenu.title}"` : 'Itens do menu'}
                        subtitle={!selectedMenu
                            ? 'Selecione um menu para gerenciar seus itens'
                            : selectedMenu.canAddItems ? `${state.items.length} item(ns)` : 'Link direto do portal: não aceita itens'}
                        actionLabel="Novo item"
                        onAction={() => ctrl.openItemDrawer()}
                        disabled={!selectedMenu?.canAddItems}
                    />
                    {state.loadingItems && <LinearProgress sx={{ mb: 1 }} />}
                    {!selectedMenu ? (
                        <EmptyState icon={<MenuIcon />} message="Selecione um menu à esquerda" />
                    ) : !selectedMenu.canAddItems ? (
                        <EmptyState icon={<LinkIcon />} message={`"${selectedMenu.title}" é um link direto do portal e não possui itens`} />
                    ) : !state.loadingItems && state.items.length === 0 ? (
                        <EmptyState icon={<LinkIcon />} message="Este menu ainda não possui itens" actionLabel="Adicionar item" onAction={() => ctrl.openItemDrawer()} />
                    ) : (
                        <SortableList
                            items={state.items}
                            getId={(i) => i.id}
                            onReorder={ctrl.handleReorderItems}
                            renderItem={(item, dragHandle) => (
                                <MenuRow
                                    dragHandle={dragHandle}
                                    title={item.title}
                                    slug={item.slug}
                                    url={item.isExternal || item.key ? item.url : undefined}
                                    customPageId={item.customPageId}
                                    isDefault={item.isDefault}
                                    isVisible={item.isVisible}
                                    canEdit={item.canEdit}
                                    onToggleVisibility={() => ctrl.handleToggleItemVisibility(item)}
                                    onEdit={() => ctrl.openItemDrawer(item)}
                                    onDelete={() => ctrl.handleDeleteItem(item)}
                                />
                            )}
                        />
                    )}
                </Paper>
            </Grid>

            <MenuDrawer
                open={state.menuDrawer.open}
                variant="menu"
                initial={state.menuDrawer.menu}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeMenuDrawer}
                onSave={ctrl.handleSaveMenu}
            />
            <MenuDrawer
                open={state.itemDrawer.open}
                variant="item"
                initial={state.itemDrawer.item}
                parentTitle={selectedMenu?.title}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeItemDrawer}
                onSave={ctrl.handleSaveItem}
            />
        </React.Fragment>
    );
}
